/* KrakenTheMeta free tools — the shared logic, no DOM: decklist parsing, the Scryfall batch lookup,
 * card roles, deck analysis (ratios by format, a Commander bracket estimate), exact draw odds, pricing.
 * Used by /mtg-deck-analyzer, /mtg-odds and /mtg-budget-swaps (bundled), and by tools/test-tools.cjs in
 * Node: require() returns the same object the pages get as KD.tools.
 */
(function (root) {
  "use strict";

  var T = {};

  // ================================================================ decklist parsing
  // Plain names; "4x" / "4 x" / "4 " quantities; Arena/MTGO/Moxfield/Archidekt suffixes like
  // "(C21) 263", "(PLST) C21-263", "*F*", "[Foil]", "[Commander{top}]", "<tags>", "# notes"; section headers
  // (a Commander block marks commanders; Sideboard / Maybeboard / Companion blocks and "SB:" lines are kept
  // apart); duplicate lines merge. Nothing is invented: every row is a line the user pasted.
  var HEADER = /^(commanders?|deck|main ?deck|mainboard|sideboard|side ?board|maybe ?board|companions?|considering|lands?|creatures?|spells?|artifacts?|enchantments?|planeswalkers?|instants?|sorcery|sorceries|battles?|tokens?|about|other)\s*(\(\d+\))?\s*:?\s*$/i;
  var OUTSIDE = /^(sideboard|side ?board|maybe ?board|companions?|considering|tokens?)/i;

  function cleanName(s) {
    var tags = [], prev;
    do {
      prev = s;
      s = s.replace(/\s*\(([A-Za-z0-9]{2,6})\)\s*[A-Za-z0-9★★-]*\s*$/, "")  // (SET) 263 / (PLST) C21-263
        .replace(/\s*\[([^\]]*)\]\s*$/, function (m, t) { tags.push(t); return ""; }) // [Foil] [Commander{top}]
        .replace(/\s*\*[^*]*\*\s*$/, function (m) { tags.push(m); return ""; })  // *F* *CMDR*
        .replace(/\s*<[^>]*>\s*$/, "")                                             // <tags>
        .replace(/\s+#.*$/, "")                                                    // # notes
        .replace(/\s+\^[^^]*\^\s*$/, "")                                           // ^tag^
        .trim();
    } while (s !== prev);
    return { name: s, tags: tags };
  }

  T.parseDecklist = function (raw) {
    var out = { lineCount: 0, rows: [], sideboard: [], skipped: 0, commanders: [] };
    if (!raw || !String(raw).trim()) return out;
    var text = String(raw).replace(/^﻿/, "").replace(/\r\n?/g, "\n").replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
    var lines = text.split("\n");
    out.lineCount = lines.length;
    var main = new Map(), side = new Map(), section = "main", cmdInSection = 0;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) { if (section === "commander") section = "main"; out.skipped++; continue; }
      if (/^(\/\/|#)/.test(line)) { out.skipped++; continue; }
      if (HEADER.test(line)) {
        section = /^commander/i.test(line) ? "commander" : OUTSIDE.test(line) ? "outside" : "main";
        cmdInSection = 0;
        out.skipped++;
        continue;
      }
      var sb = /^sb:\s*/i.test(line);
      line = line.replace(/^sb:\s*/i, "");
      var qty = 1, m = line.match(/^(\d+)\s*[xX]?\s+(.+)$/);
      if (m) { qty = parseInt(m[1], 10); line = m[2].trim(); }
      if (!(qty > 0) || qty > 250) { out.skipped++; continue; }
      var c = cleanName(line);
      if (!c.name) { out.skipped++; continue; }
      var tagged = c.tags.some(function (t) { return /commander|\bcmdr\b/i.test(t); });
      var isCmd = !sb && (tagged || (section === "commander" && cmdInSection < 2));
      if (section === "commander" && !tagged) cmdInSection++;
      var map = sb || section === "outside" ? side : main;
      var key = c.name.toLowerCase();
      if (map.has(key)) { var r = map.get(key); r.quantity += qty; r.commander = r.commander || isCmd; }
      else map.set(key, { name: c.name, quantity: qty, commander: isCmd });
    }
    out.rows = Array.from(main.values());
    out.sideboard = Array.from(side.values());
    out.commanders = out.rows.filter(function (r) { return r.commander; }).map(function (r) { return r.name; });
    return out;
  };

  // A pasted list back as text (for hand-offs to the dashboard and the other tools).
  T.listText = function (rows) {
    var cmd = rows.filter(function (r) { return r.commander; });
    var line = function (r) { return r.quantity + " " + r.name; };
    if (!cmd.length) return rows.map(line).join("\n");
    return "Commander\n" + cmd.map(line).join("\n") + "\n\nDeck\n" + rows.filter(function (r) { return !r.commander; }).map(line).join("\n");
  };

  // ================================================================ Scryfall
  function frontFace(n) { return (String(n).split("//")[0] || n).trim() || n; }
  T.frontFace = frontFace;
  function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  // rows → { matched: [{row, card}], unmatched: [row] }, 75 names a request, retries on 5xx.
  // Scryfall allows 2 /cards/collection requests a second and a 429 locks the browser out for 30 s
  // (2026-10-05): requests are 550 ms apart, and a 429 ends the lookup instead of retrying into it.
  T.resolveCards = async function (rows, onProgress) {
    var matched = [], unmatched = [], BATCH = 75, done = 0;
    for (var i = 0; i < rows.length; i += BATCH) {
      var slice = rows.slice(i, i + BATCH), data = null;
      var identifiers = slice.map(function (r) { return { name: frontFace(r.name) }; });
      for (var attempt = 0; attempt < 3; attempt++) {
        try {
          var resp = await fetch("https://api.scryfall.com/cards/collection", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ identifiers: identifiers }) });
          if (resp.status === 429) break;
          if (resp.status >= 500) {
            var ra = parseInt(resp.headers.get("Retry-After") || "", 10);
            await delay(isNaN(ra) ? 350 * (attempt + 1) : ra * 1000);
            continue;
          }
          data = resp.ok ? await resp.json() : { data: [] };
          break;
        } catch (e) { await delay(350 * (attempt + 1)); }
      }
      if (!data) throw new Error("Scryfall didn't answer");
      var cards = data.data || [], used = new Set();
      slice.forEach(function (r) {
        var full = r.name.toLowerCase(), qn = frontFace(r.name).toLowerCase(), found = null;
        for (var k = 0; k < cards.length; k++) {
          if (used.has(k)) continue;
          var cn = cards[k].name.toLowerCase();
          if (cn === full || cn === qn || cn.split(" // ")[0] === qn) { used.add(k); found = cards[k]; break; }
        }
        if (found) matched.push({ row: r, card: found }); else unmatched.push(r);
      });
      done += slice.length;
      if (onProgress) onProgress(done, rows.length);
      if (i + BATCH < rows.length) await delay(550);
    }
    return { matched: matched, unmatched: unmatched };
  };

  /* Scryfall's default printing of a reprinted card is often the newest one, which may not have a price
   * yet (Sol Ring's latest Commander reprint, new basics). For every matched card without a USD price,
   * look up its cheapest priced printing (prefer:usd-low, 20 names a request) and use those prices. */
  T.repriceUnpriced = async function (matched) {
    var need = {}, names = [];
    matched.forEach(function (x) {
      if (T.priceOf(x.card) === null && !need[x.card.name.toLowerCase()]) { need[x.card.name.toLowerCase()] = []; names.push(x.card.name); }
      if (need[x.card.name.toLowerCase()]) need[x.card.name.toLowerCase()].push(x);
    });
    for (var i = 0; i < names.length; i += 20) {
      var q = "(" + names.slice(i, i + 20).map(function (n) { return '!"' + frontFace(n).replace(/"/g, "") + '"'; }).join(" or ") + ") prefer:usd-low";
      try {
        var r = await fetch("https://api.scryfall.com/cards/search?unique=cards&q=" + encodeURIComponent(q), { headers: { Accept: "application/json" } });
        var j = r.ok ? await r.json() : { data: [] };
        (j.data || []).forEach(function (c) {
          var list = need[c.name.toLowerCase()] || need[frontFace(c.name).toLowerCase()];
          if (list && T.priceOf(c) !== null) list.forEach(function (x) { x.card = Object.assign({}, x.card, { prices: c.prices }); });
        });
      } catch (e) { /* those cards just stay unpriced */ }
      if (i + 20 < names.length) await delay(550);
    }
    return matched;
  };

  // ================================================================ card roles
  function oracleOf(card) {
    var o = card.oracle_text || "";
    if (!o && Array.isArray(card.card_faces)) o = card.card_faces.map(function (f) { return f.oracle_text || ""; }).join("\n");
    return o.toLowerCase();
  }
  function frontType(card) { return String(card.type_line || (card.card_faces && card.card_faces[0] && card.card_faces[0].type_line) || "").split("//")[0].toLowerCase(); }
  function colorsOf(card) {
    if (Array.isArray(card.colors)) return card.colors;
    if (Array.isArray(card.card_faces) && card.card_faces[0] && Array.isArray(card.card_faces[0].colors)) return card.card_faces[0].colors;
    return [];
  }
  T.oracleOf = oracleOf;

  var BASICS = { plains: 1, island: 1, swamp: 1, mountain: 1, forest: 1, wastes: 1, "snow-covered plains": 1, "snow-covered island": 1, "snow-covered swamp": 1, "snow-covered mountain": 1, "snow-covered forest": 1 };
  T.isLand = function (card) {
    if (frontType(card).indexOf("land") !== -1) return true;
    return !!BASICS[String(card.name || "").toLowerCase()];
  };
  T.isBasic = function (name) { return !!BASICS[String(name || "").toLowerCase()]; };

  T.classify = function (card) {
    var tags = { land: T.isLand(card) };
    if (card.game_changer) tags.gameChanger = true;
    if (tags.land) return tags;
    var o = oracleOf(card);
    if (/\badd\s+(\{|one|two|three|four|x\b|that much|an additional)/.test(o) ||
      (/search your library for .*(land|forest|island|swamp|mountain|plains)/.test(o) && /onto the battlefield|to the battlefield/.test(o)) ||
      (/\btreasure\b/.test(o) && /create|token/.test(o)) ||
      /put (a|one|two|that) .*land.* onto the battlefield/.test(o)) tags.ramp = true;
    if (/draws? (a|one|two|three|x|that many|\w+) cards?/.test(o) || /\bdraw a card\b/.test(o)) tags.draw = true;
    var wipe = /destroy all|exile all|destroy each|exile each/.test(o) || /all creatures get [-−]\d+\/[-−]\d+/.test(o) ||
      /deals? \d+ damage to (each|all|every) (creature|player|opponent)/.test(o) || /each (player|opponent) sacrifices/.test(o);
    if (wipe) tags.wipe = true;
    // removal: the common wordings, including "an opponent controls" shrink effects (Nowhere to Run,
    // Infernal Grasp-style "up to one target"), X damage, fight/bite and edicts that exile.
    if (!wipe && (/(destroy|exile) (up to (one|two|three|x) )?(other )?target/.test(o) ||
      /deals? (\d+|x|that much) damage to (up to one |another )?(target|any target)/.test(o) ||
      /deals? damage equal to [^.]*? to (up to one |another )?(target|any target)/.test(o) || /counter target/.test(o) ||
      /return (up to (one|two) )?target (creature|permanent|nonland permanent|artifact|enchantment|planeswalker|spell)/.test(o) ||
      /target creature( or planeswalker)?( an opponent controls| you don't control| your opponents control)? gets [-−](\d+|x)\/[-−](\d+|x)/.test(o) ||
      /fights? (up to one |another )?target/.test(o) || /target (player|opponent) (sacrifices|exiles a creature)/.test(o) ||
      /put target (creature|nonland permanent|permanent|artifact|enchantment)[^.]*? on the (top|bottom) of (its|their) owner's library/.test(o))) tags.removal = true;
    // bracket signals: tutors (not land-only searches), extra turns, mass land destruction
    var tut = o.match(/search your library for (?:a|an|up to \w+) ([^.]*?)cards?\b/);
    if (tut && !/\b(land|basic|plains|island|swamp|mountain|forest)\b/.test(tut[1])) tags.tutor = true;
    if (/takes? an extra turn/.test(o)) tags.extraTurn = true;
    if (/destroy all [^.]*lands|each player sacrifices [^.]*lands|destroy all permanents/.test(o)) tags.mld = true;
    return tags;
  };

  // ================================================================ deck analysis
  // Recommended ranges by format. Commander numbers are the usual 100-card template; 60- and 40-card
  // ranges are looser, since constructed archetypes differ a lot.
  T.REC = {
    commander: {
      lands: { min: 36, max: 39, label: "36–38", tip: "A consistent Commander mana base is usually 36–38 lands (a few less with lots of cheap ramp)." },
      ramp: { min: 10, max: 99, label: "10+", tip: "About 10 ramp pieces (mana rocks, dorks, land ramp) so you hit your curve." },
      draw: { min: 8, max: 99, label: "8–12", tip: "8–12 card-draw sources so you refill and don't run out of gas." },
      removal: { min: 8, max: 99, label: "8–12", tip: "8–12 pieces of targeted interaction to answer threats." },
      wipe: { min: 2, max: 99, label: "2–5", tip: "2–5 board wipes to reset against wide boards." }
    },
    constructed: {
      lands: { min: 20, max: 27, label: "20–26", tip: "Most 60-card decks run 20–26 lands: fewer for low curves, more for control and ramp." },
      ramp: { min: 0, max: 99, label: "any", tip: "Ramp is optional in 60-card formats; it matters mostly for big-mana decks." },
      draw: { min: 4, max: 99, label: "4+", tip: "A few card-advantage pieces keep you from running out of cards." },
      removal: { min: 6, max: 99, label: "6+", tip: "Around 6–10 interaction spells for most non-aggro decks." },
      wipe: { min: 0, max: 99, label: "0–4", tip: "Board wipes are a control tool; aggro decks can skip them." }
    },
    limited: {
      lands: { min: 16, max: 18, label: "16–18", tip: "40-card decks usually play 16–18 lands (17 is the classic)." },
      ramp: { min: 0, max: 99, label: "any", tip: "Ramp is optional in 40-card decks." },
      draw: { min: 1, max: 99, label: "1+", tip: "Any card advantage helps in Limited." },
      removal: { min: 3, max: 99, label: "3+", tip: "Aim for as much good removal as your colors give you." },
      wipe: { min: 0, max: 99, label: "0–2", tip: "Board wipes are rare in Limited." }
    }
  };
  T.formatOf = function (total, hasCommander) {
    if (hasCommander || total >= 90) return "commander";
    if (total >= 58) return "constructed";
    return "limited";
  };

  // The Commander brackets (Wizards, 2025) from what a list can show: Game Changers (Scryfall marks them),
  // mass land destruction, extra turns, tutors. Combos and intent can't be read from a list.
  T.bracket = function (a) {
    var reasons = [], level;
    if (a.gameChangers.length >= 4 || a.mld.length || a.extraTurns.length >= 2) {
      level = 4;
      if (a.gameChangers.length >= 4) reasons.push(a.gameChangers.length + " Game Changers (more than 3)");
      if (a.mld.length) reasons.push("mass land destruction: " + a.mld.join(", "));
      if (a.extraTurns.length >= 2) reasons.push(a.extraTurns.length + " extra-turn cards");
    } else if (a.gameChangers.length >= 1 || a.tutors.length > 3 || a.extraTurns.length === 1) {
      level = 3;
      if (a.gameChangers.length) reasons.push(a.gameChangers.length + " Game Changer" + (a.gameChangers.length === 1 ? "" : "s") + " (up to 3 allowed)");
      if (a.tutors.length > 3) reasons.push(a.tutors.length + " tutors");
      if (a.extraTurns.length === 1) reasons.push("an extra-turn card");
    } else {
      level = 2;
      reasons.push("no Game Changers, mass land destruction or extra turns");
    }
    var names = { 2: "Core", 3: "Upgraded", 4: "Optimized" };
    return { level: level, name: names[level], label: (level === 2 ? "2 or lower" : level === 4 ? "4 or higher" : "3") + " (" + names[level] + ")", reasons: reasons };
  };

  // matched: [{row, card}] → counts, curve, colors, ratios, bracket signals. Quantities count.
  T.analyze = function (matched, opts) {
    opts = opts || {};
    var a = { totalCards: 0, lands: 0, nonlands: 0, avgMv: 0, buckets: [0, 0, 0, 0, 0, 0, 0, 0], colorCount: { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 },
      ramp: 0, draw: 0, removal: 0, wipe: 0, gameChangers: [], tutors: [], extraTurns: [], mld: [], commanders: [] };
    var mvSum = 0;
    matched.forEach(function (x) {
      var q = x.row.quantity || 1, card = x.card, tags = T.classify(card);
      a.totalCards += q;
      if (x.row.commander) a.commanders.push(card.name);
      if (tags.gameChanger) a.gameChangers.push(card.name);
      if (tags.land) { a.lands += q; return; }
      a.nonlands += q;
      var cmc = typeof card.cmc === "number" ? card.cmc : 0;
      mvSum += cmc * q;
      a.buckets[cmc >= 7 ? 7 : Math.max(0, Math.round(cmc))] += q;
      var cols = colorsOf(card);
      if (!cols.length) a.colorCount.C += q; else cols.forEach(function (c) { if (a.colorCount[c] !== undefined) a.colorCount[c] += q; });
      if (tags.ramp) a.ramp += q;
      if (tags.draw) a.draw += q;
      if (tags.removal) a.removal += q;
      if (tags.wipe) a.wipe += q;
      if (tags.tutor) a.tutors.push(card.name);
      if (tags.extraTurn) a.extraTurns.push(card.name);
      if (tags.mld) a.mld.push(card.name);
    });
    a.avgMv = a.nonlands ? mvSum / a.nonlands : 0;
    a.format = opts.format || T.formatOf(a.totalCards, a.commanders.length > 0);
    a.rec = T.REC[a.format];
    a.bracket = a.format === "commander" ? T.bracket(a) : null;
    a.gaps = ["lands", "ramp", "draw", "removal", "wipe"].filter(function (k) { return a[k] < a.rec[k].min; });
    a.landsHigh = a.lands > a.rec.lands.max;
    return a;
  };
  T.status = function (count, rec) {
    if (count < rec.min) return "low";
    if (count > rec.max) return "high";
    return "good";
  };

  // ================================================================ draw odds (exact hypergeometric)
  var _lf = [0, 0];
  function lf(n) { for (var i = _lf.length; i <= n; i++) _lf.push(_lf[i - 1] + Math.log(i)); return _lf[n]; }
  function logC(n, k) { if (k < 0 || k > n || n < 0) return -Infinity; return lf(n) - lf(k) - lf(n - k); }
  T.hyperPMF = function (N, K, n, i) {
    var l = logC(K, i) + logC(N - K, n - i) - logC(N, n);
    return l === -Infinity ? 0 : Math.exp(l);
  };
  // P(at least k successes) drawing n from N with K successes in the deck
  T.hyperAtLeast = function (N, K, n, k) {
    var lo = Math.max(0, n - (N - K)), hi = Math.min(n, K), p = 0;
    for (var i = Math.max(k, lo); i <= hi; i++) p += T.hyperPMF(N, K, n, i);
    return Math.min(1, Math.max(0, p));
  };
  T.validateHyper = function (N, K, n, k) {
    if (![N, K, n, k].every(Number.isFinite)) return "Enter a whole number in every field.";
    if (N < 1 || N > 1000) return "Deck size must be between 1 and 1,000.";
    if (K < 0 || K > N) return "Copies in the deck must be between 0 and the deck size.";
    if (n < 1 || n > N) return "Cards drawn must be between 1 and the deck size.";
    if (k < 0 || k > n) return "“At least” must be between 0 and the cards drawn.";
    return null;
  };
  // P(exactly i) for i = 0..n (the opening-hand land histogram)
  T.distribution = function (N, K, n) {
    var out = [];
    for (var i = 0; i <= n; i++) out.push(T.hyperPMF(N, K, n, i));
    return out;
  };
  // P(at least k hits by turn t), t = 1..turns, on the play (no draw on turn 1) and on the draw
  T.byTurn = function (N, K, k, turns, hand) {
    hand = hand || 7;
    var rows = [];
    for (var t = 1; t <= turns; t++) {
      var play = Math.min(N, hand + t - 1), draw = Math.min(N, hand + t);
      rows.push({ turn: t, play: T.hyperAtLeast(N, K, play, k), draw: T.hyperAtLeast(N, K, draw, k) });
    }
    return rows;
  };

  // London mulligan: a fresh 7, then `bottom` of them go under (highest mana value first, spells before
  // lands on ties). In multiplayer Commander the first mulligan is free (bottom 0).
  T.bottomCount = function (mulls, freeFirst) { return Math.max(0, freeFirst ? mulls - 1 : mulls); };
  T.pickBottom = function (seven, bottom) {
    var order = seven.map(function (c, i) { return { c: c, i: i }; })
      .sort(function (a, b) { return (b.c.cmc - a.c.cmc) || ((a.c.land ? 1 : 0) - (b.c.land ? 1 : 0)); });
    var set = {};
    for (var b = 0; b < Math.min(bottom, seven.length); b++) set[order[b].i] = true;
    return set;
  };
  T.MAX_MULLIGANS = 6;

  // ================================================================ pricing
  T.priceOf = function (card) {
    var p = card && card.prices;
    if (!p) return null;
    var usd = p.usd != null ? parseFloat(p.usd) : NaN;
    if (!isNaN(usd)) return usd;
    var foil = p.usd_foil != null ? parseFloat(p.usd_foil) : NaN;
    return isNaN(foil) ? null : foil;
  };
  T.roleOf = function (card) {
    var type = frontType(card);
    if (type.indexOf("land") !== -1) return "land";
    var tags = T.classify(card);
    if (tags.ramp) return "ramp";
    if (tags.wipe) return "board wipe";
    if (tags.removal) return "removal";
    if (tags.draw) return "card draw";
    if (tags.tutor) return "tutor";
    if (type.indexOf("creature") !== -1) return "creature";
    if (type.indexOf("planeswalker") !== -1) return "planeswalker";
    if (type.indexOf("instant") !== -1 || type.indexOf("sorcery") !== -1) return "spell";
    if (type.indexOf("artifact") !== -1) return "artifact";
    if (type.indexOf("enchantment") !== -1) return "enchantment";
    return "card";
  };
  T.isCommanderCard = function (card) {
    var t = String(card.type_line || "").toLowerCase(), o = oracleOf(card);
    return (t.indexOf("legendary") !== -1 && t.indexOf("creature") !== -1) || o.indexOf("can be your commander") !== -1;
  };

  // matched → the budget model: total, priced/unpriced, the priciest swappable cards (never the
  // commander, never basics, $2 and up), color identity (the commander's, else the deck's).
  T.buildModel = function (matched) {
    var m = { totalCards: 0, totalPrice: 0, priced: 0, unpriced: 0, rows: [], swappable: [], commanders: [], deckColorIdentity: [], format: "commander", existingNames: [] };
    var colorSet = new Set(), cmdCI = null;
    var marked = matched.filter(function (x) { return x.row.commander; });
    if (!marked.length) {
      // no Commander section: a singleton legendary creature listed first in a ~100-card list
      var total = matched.reduce(function (s, x) { return s + (x.row.quantity || 1); }, 0);
      if (total >= 90 && matched[0] && (matched[0].row.quantity || 1) === 1 && T.isCommanderCard(matched[0].card)) marked = [matched[0]];
    }
    marked.forEach(function (x) {
      m.commanders.push(x.card.name);
      cmdCI = (cmdCI || []).concat((x.card.color_identity || []).map(function (c) { return String(c).toUpperCase(); }));
    });
    matched.forEach(function (x) {
      var card = x.card, q = x.row.quantity || 1, lc = card.name.toLowerCase(), pr = T.priceOf(card);
      m.totalCards += q;
      m.existingNames.push(card.name);
      (card.color_identity || []).forEach(function (c) { colorSet.add(String(c).toUpperCase()); });
      if (pr === null) m.unpriced += q; else { m.priced += q; m.totalPrice += pr * q; }
      m.rows.push({ name: card.name, lc: lc, qty: q, price: pr, role: T.roleOf(card), isBasic: T.isBasic(lc), commander: m.commanders.indexOf(card.name) !== -1, card: card });
    });
    m.format = T.formatOf(m.totalCards, m.commanders.length > 0) === "commander" ? "commander" : "standard";
    m.deckColorIdentity = "WUBRG".split("").filter(function (c) { return (cmdCI ? cmdCI.indexOf(c) !== -1 : colorSet.has(c)); });
    m.swappable = m.rows.filter(function (r) { return !r.commander && !r.isBasic && r.price !== null && r.price >= 2; })
      .sort(function (a, b) { return b.price * b.qty - a.price * a.qty || b.price - a.price; });
    return m;
  };
  // The AI's per-copy savings, counted for every copy in the deck.
  T.totalSavings = function (swaps, rows) {
    var qty = {};
    (rows || []).forEach(function (r) { qty[r.lc] = r.qty; });
    return (swaps || []).reduce(function (s, sw) {
      var best = sw.bestSaved != null ? sw.bestSaved : (sw.suggestions && sw.suggestions[0] ? sw.suggestions[0].saved : 0);
      return s + (Number(best) || 0) * (qty[String(sw.original).toLowerCase()] || 1);
    }, 0);
  };

  if (typeof module !== "undefined" && module.exports) module.exports = T;
  if (root) { root.KD = root.KD || {}; root.KD.tools = T; }
})(typeof window !== "undefined" ? window : null);
